import React from 'react';
import { Modal, View, Text, Pressable, StyleSheet, Image } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { toast } from 'react-toastify';

type IconName = React.ComponentProps<typeof MaterialCommunityIcons>['name'];

interface ShareModalProps {
    visible: boolean;
    onClose: () => void;
    title: string;
    description?: string;
    imageUrl?: string;
    shareUrl: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
    visible,
    onClose,
    title,
    description,
    imageUrl,
    shareUrl
}) => {
    const handleCopyLink = () => {
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
        onClose();
    };

    const handleEmail = () => {
        const subject = encodeURIComponent(title);
        const body = encodeURIComponent(`Check out this on SnapCamp:\n\n${title}\n${description || ''}\n\n${shareUrl}`);
        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    };

    const handleWhatsApp = () => {
        const text = encodeURIComponent(`Check out ${title} on SnapCamp:\n${shareUrl}`);
        window.open(`https://wa.me/?text=${text}`, '_blank');
    };

    const handleFacebook = () => {
        const url = encodeURIComponent(shareUrl);
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
    };

    const handleTwitter = () => {
        const text = encodeURIComponent(`Check out ${title} on SnapCamp`);
        const url = encodeURIComponent(shareUrl);
        window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
    };

    const handleMessages = () => {
        const text = encodeURIComponent(`Check out ${title} on SnapCamp:\n${shareUrl}`);
        window.open(`sms:?body=${text}`, '_blank');
    };

    const handleMessenger = () => {
        const url = encodeURIComponent(shareUrl);
        window.open(`https://www.facebook.com/dialog/send?link=${url}&app_id=YOUR_APP_ID&redirect_uri=${shareUrl}`, '_blank');
    };

    const handleEmbed = () => {
        const embedCode = `<iframe src="${shareUrl}" width="100%" height="600" frameborder="0"></iframe>`;
        navigator.clipboard.writeText(embedCode);
        toast.success('Embed code copied to clipboard!');
        onClose();
    };

    const shareOptions: Array<{
        id: string;
        icon: IconName;
        label: string;
        onPress: () => void;
    }> = [
        { id: 'copy', icon: 'content-copy', label: 'Copy Link', onPress: handleCopyLink },
        { id: 'email', icon: 'email-outline', label: 'Email', onPress: handleEmail },
        { id: 'messages', icon: 'message-text-outline', label: 'Messages', onPress: handleMessages },
        { id: 'whatsapp', icon: 'whatsapp', label: 'WhatsApp', onPress: handleWhatsApp },
        { id: 'messenger', icon: 'facebook-messenger', label: 'Messenger', onPress: handleMessenger },
        { id: 'facebook', icon: 'facebook', label: 'Facebook', onPress: handleFacebook },
        { id: 'twitter', icon: 'twitter', label: 'Twitter', onPress: handleTwitter },
        { id: 'embed', icon: 'code-tags', label: 'Embed', onPress: handleEmbed },
    ];

    return (
        <Modal
            visible={visible}
            animationType="fade"
            transparent={true}
            onRequestClose={onClose}
        >
            <Pressable style={styles.overlay} onPress={onClose}>
                <Pressable style={styles.modalContainer} onPress={(e) => e.stopPropagation()}>
                    {/* Close button */}
                    <Pressable style={styles.closeButton} onPress={onClose}>
                        <MaterialCommunityIcons name="close" size={24} color="#666" />
                    </Pressable>

                    {/* Header */}
                    <Text style={styles.modalTitle}>Share this SnapCamp</Text>

                    {/* Preview Card */}
                    <View style={styles.previewCard}>
                        {imageUrl && (
                            <Image 
                                source={{ uri: imageUrl }} 
                                style={styles.previewImage}
                                resizeMode="cover"
                            />
                        )}
                        <View style={styles.previewContent}>
                            <Text style={styles.previewTitle} numberOfLines={2}>{title}</Text>
                            {description && (
                                <Text style={styles.previewDescription} numberOfLines={2}>
                                    {description}
                                </Text>
                            )}
                        </View>
                    </View>

                    {/* Share Options Grid */}
                    <View style={styles.optionsGrid}>
                        {shareOptions.map((option, index) => (
                            <Pressable
                                key={option.id}
                                style={({ pressed }) => [
                                    styles.optionButton,
                                    pressed && styles.optionButtonPressed
                                ]}
                                onPress={option.onPress}
                            >
                                <View style={styles.optionIconContainer}>
                                    <MaterialCommunityIcons 
                                        name={option.icon} 
                                        size={24} 
                                        color="#222" 
                                    />
                                </View>
                                <Text style={styles.optionLabel}>{option.label}</Text>
                            </Pressable>
                        ))}
                    </View>
                </Pressable>
            </Pressable>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalContainer: {
        backgroundColor: '#fff',
        borderRadius: 16,
        padding: 24,
        width: '90%',
        maxWidth: 600,
        maxHeight: '90%',
    },
    closeButton: {
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 10,
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: '#f5f5f5',
        justifyContent: 'center',
        alignItems: 'center',
    },
    modalTitle: {
        fontSize: 22,
        fontWeight: '600',
        marginBottom: 20,
        color: '#222',
    },
    previewCard: {
        flexDirection: 'row',
        backgroundColor: '#f8f8f8',
        borderRadius: 12,
        padding: 12,
        marginBottom: 24,
        alignItems: 'center',
    },
    previewImage: {
        width: 80,
        height: 80,
        borderRadius: 8,
        marginRight: 12,
    },
    previewContent: {
        flex: 1,
    },
    previewTitle: {
        fontSize: 14,
        fontWeight: '600',
        color: '#222',
        marginBottom: 4,
    },
    previewDescription: {
        fontSize: 12,
        color: '#666',
        lineHeight: 16,
    },
    optionsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
    optionButton: {
        width: '23%',
        minWidth: 100,
        alignItems: 'center',
        padding: 12,
        borderRadius: 12,
        backgroundColor: '#f8f8f8',
    },
    optionButtonPressed: {
        backgroundColor: '#e8e8e8',
    },
    optionIconContainer: {
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: '#fff',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
    },
    optionLabel: {
        fontSize: 12,
        fontWeight: '500',
        color: '#222',
        textAlign: 'center',
    },
});

export default ShareModal;

